// Sidebar Navigation Component
// Stripe-inspired white sidebar design with collapsible product sections

import { useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { trpc } from '../../lib/trpc';

// ============================================================
// TYPES
// ============================================================

interface NavItem {
  name: string;
  href: string;
  icon: string;
}

interface NavSection {
  id: string;
  label: string;
  tooltip: string;
  productId: string;
  icon: string;
  items: NavItem[];
}

type SidebarEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'section'; section: NavSection };

// ============================================================
// SIDEBAR CONFIGURATION
// ============================================================

const sidebarConfig: SidebarEntry[] = [
  { type: 'item', item: { name: 'Dashboard', href: '/dashboard', icon: '/assets/icons/icon-dashboard.png' } },
  { type: 'item', item: { name: 'ERP Connections', href: '/erp', icon: '/assets/icons/icon-connection.png' } },
  {
    type: 'section',
    section: {
      id: 'fra',
      label: 'FRA',
      tooltip: 'Financial Reporting & Analysis',
      productId: 'fra',
      icon: '/assets/icons/icon-document.png',
      items: [
        { name: 'Reports', href: '/reports', icon: '/assets/icons/icon-document.png' },
      ],
    },
  },
  { type: 'item', item: { name: 'Install Add-in', href: '/install-addin', icon: 'svg:download' } },
  { type: 'item', item: { name: 'Settings', href: '/settings', icon: '/assets/icons/icon-settings.png' } },
];

// ============================================================
// COLLAPSE STATE HOOK
// ============================================================

const STORAGE_KEY = 'calculom-sidebar-sections';

function useSectionCollapseState(): [Record<string, boolean>, (sectionId: string) => void] {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) as Record<string, boolean> : {};
    } catch {
      return {};
    }
  });

  const toggle = useCallback((sectionId: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return [collapsed, toggle];
}

// ============================================================
// SIDEBAR COMPONENT
// ============================================================

export function Sidebar() {
  const { user } = useAuthStore();
  const { data: tenant } = trpc.tenant.getCurrent.useQuery();
  const [collapsed, toggleSection] = useSectionCollapseState();

  function hasProduct(productId: string): boolean {
    return tenant?.subscription?.products?.some(
      (p: { id: string }) => p.id === productId
    ) ?? false;
  }

  // Hide product indicators while subscription data is loading
  const subscriptionLoaded = tenant !== undefined;

  return (
    <aside className="flex w-64 flex-col bg-white border-r border-gray-200">
      {/* Empty top section - aligns with header height */}
      <div className="h-16 border-b border-gray-200" />

      {/* Logo section - below header line */}
      <div className="flex items-center justify-center px-6 py-5">
        <img
          src="/assets/logo/calculom.png"
          alt="Calculom"
          className="w-[100px] h-auto object-contain"
        />
      </div>

      {/* Tenant name */}
      <div className="px-6 py-4 border-y border-gray-200">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</p>
        <p className="text-sm font-semibold text-gray-900 truncate mt-1">{user?.tenantName ?? 'Loading...'}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4">
        <ul className="space-y-1">
          {sidebarConfig.map((entry) => {
            if (entry.type === 'item') {
              return (
                <li key={entry.item.name}>
                  <NavLink
                    to={entry.item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                  >
                    {entry.item.icon === 'svg:download' ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    ) : (
                      <img src={entry.item.icon} alt="" className="h-5 w-5" />
                    )}
                    {entry.item.name}
                  </NavLink>
                </li>
              );
            }

            const { section } = entry;
            const isCollapsed = collapsed[section.id] ?? false;
            const isLicensed = hasProduct(section.productId);

            return (
              <li key={section.id} className="pt-3">
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  title={section.tooltip}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  {/* Chevron */}
                  <svg
                    className={`h-3 w-3 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                      isCollapsed ? '-rotate-90' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>

                  <img src={section.icon} alt="" className="h-4 w-4" />
                  <span>{section.label}</span>

                  {/* Unlicensed indicator */}
                  {subscriptionLoaded && !isLicensed && (
                    <span className="ml-auto inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-600">
                      Add
                    </span>
                  )}
                </button>

                {/* Collapsible children */}
                {!isCollapsed && (
                  <ul className="mt-0.5 space-y-0.5">
                    {section.items.map((item) => (
                      <li key={item.name}>
                        <NavLink
                          to={item.href}
                          className={({ isActive }) =>
                            `flex items-center gap-3 rounded-lg pl-8 pr-3 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`
                          }
                        >
                          <img src={item.icon} alt="" className="h-5 w-5" />
                          {item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User info */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium flex-shrink-0">
            {user?.displayName?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.displayName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
